import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { collection, query, onSnapshot, doc, updateDoc, serverTimestamp, writeBatch, where } from "firebase/firestore";
import { db } from '../config/firebase';
import { theme } from '../config/theme';
import { formOptions } from '../constants/formOptions';
import { formatPrice, formatCurrency, formatNumberForDisplay, formatNumberForInput, parseNumberForState, parseAmountForState, formatComitenteForDisplay } from '../utils/formatters';
import { renderCommentCell } from '../utils/uiHelpers';
import { formatHoraForDisplay } from '../utils/dateHelpers';
import { subscribeByComitentes } from '../services/comitentesQueryService';
import { filterMisClientes } from '../utils/saldosScope';
import { comercialPorDefecto } from '../utils/rolesUsuario';
import { compareValues } from '../utils/sortHelpers';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useStackedTable } from '../hooks/useStackedTable';
import { useMobileCollapse } from '../hooks/useMobileCollapse';
import { useNombresComerciales } from '../hooks/useNombresComerciales';
import TopScrollbar from './TopScrollbar';
import CollapsibleHeader from './common/CollapsibleHeader';
import { PencilIcon, TrashIcon } from './Icons';
import A3WarningModal from './A3WarningModal';
import DuplicateWarningModal from './DuplicateWarningModal';
import {
    findDuplicates, buildDuplicateGroups, getOperacionDuplicateKey,
} from '../utils/duplicateDetection';
import Modal from './common/Modal';

// --- ESTILOS DEL COMPONENTE BASADOS EN EL TEMA ---
const styles = {
    container: { fontFamily: theme.typography.fontFamily },
    headerContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    mainTitle: { fontFamily: theme.typography.titleFamily, fontSize: '2rem', color: theme.colors.secondary, margin: 0 },
    button: {
        fontFamily: theme.typography.fontFamily, backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        border: 'none', borderRadius: theme.borderRadius, padding: '0.75rem 1.5rem', fontSize: '0.9rem',
        fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s', display: 'flex',
        alignItems: 'center', gap: '0.5rem',
    },
    secondaryButton: { backgroundColor: theme.colors.border, color: theme.colors.secondary },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' },
    tableContainer: {
        overflowX: 'auto', backgroundColor: theme.colors.surface, borderRadius: theme.borderRadius,
        boxShadow: theme.shadows.soft,
    },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '1600px' },
    th: {
        backgroundColor: '#F9FAFB', padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600',
        textTransform: 'uppercase', color: theme.colors.secondary, borderBottom: `1px solid ${theme.colors.border}`,
    },
    td: { padding: '1rem', borderBottom: `1px solid ${theme.colors.border}`, fontSize: '0.875rem', verticalAlign: 'middle' },
    // Columnas de contenido corto (ej: Hora): ocupan el mínimo ancho posible.
    narrowCol: { paddingLeft: '0.4rem', paddingRight: '0.4rem', width: '1%', whiteSpace: 'nowrap' },
    enProcesoRow: { backgroundColor: theme.colors.warning },
    hechaRow: { backgroundColor: theme.colors.success },
    rechazadaRow: { backgroundColor: theme.colors.error },
    clickableText: { color: theme.colors.primary, cursor: 'pointer', textDecoration: 'underline' },
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(17, 24, 39, 0.8)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000,
    },
    modalContent: {
        backgroundColor: theme.colors.background, padding: '1.25rem 2rem', borderRadius: '8px', width: '90%',
        maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', boxShadow: theme.shadows.soft,
        display: 'flex', flexDirection: 'column',
    },
    modalScrollArea: { overflowY: 'auto', flex: '1 1 auto', minHeight: 0 },
    modalTitle: { fontFamily: theme.typography.titleFamily, fontSize: '1.5rem', color: theme.colors.primary, margin: '0 0 0.75rem 0', flexShrink: 0 },
    sectionHeader: {
        gridColumn: '1 / -1', fontFamily: theme.typography.titleFamily, fontSize: '0.8rem', fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.primary,
        borderBottom: `1px solid ${theme.colors.border}`, paddingBottom: '0.4rem', marginBottom: '-0.25rem',
    },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.85rem 1.5rem' },
    formGroup: { gridColumn: 'span 1' },
    formGroupFull: { gridColumn: 'span 3' },
    formGroupDouble: { gridColumn: 'span 2' },
    formGroupHalf: { gridColumn: 'span 1' },
    label: { display: 'block', marginBottom: '0.3rem', fontSize: '0.875rem', fontWeight: '500', color: theme.colors.secondary },
    input: {
        width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: theme.typography.fontFamily,
    },
    select: {
        width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', backgroundColor: theme.colors.surface, fontFamily: theme.typography.fontFamily, boxSizing: 'border-box',
    },
    textarea: {
        width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: theme.typography.fontFamily, minHeight: '52px', resize: 'vertical',
    },
    modalActions: { marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: `1px solid ${theme.colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: '1rem', flexShrink: 0 },
    filterContainer: {
        marginBottom: '1rem', backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius, boxShadow: theme.shadows.soft,
        borderLeft: `3px solid ${theme.colors.primary}`,
    },
    filterHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1.25rem', borderBottom: `1px solid ${theme.colors.border}`,
    },
    filterHeaderLeft: {
        display: 'flex', alignItems: 'center', gap: '0.5rem',
    },
    filterTitle: {
        fontFamily: theme.typography.titleFamily, fontSize: '0.85rem', fontWeight: '600',
        color: theme.colors.primary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    filterBadge: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        fontSize: '0.65rem', fontWeight: '700', borderRadius: '10px',
        minWidth: '18px', height: '18px', padding: '0 5px',
    },
    filterGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '0.75rem', padding: '1rem 1.25rem',
    },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
    filterLabel: {
        fontSize: '0.68rem', fontWeight: '600', textTransform: 'uppercase',
        color: theme.colors.textSecondary, letterSpacing: '0.05em',
    },
    filterInput: {
        padding: '0.5rem 0.65rem', border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius, fontSize: '0.8rem', fontFamily: theme.typography.fontFamily,
        boxSizing: 'border-box', width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
        outline: 'none', color: theme.colors.secondary,
    },
    filterInputActive: {
        borderColor: theme.colors.primary, boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)',
    },
    filterSelect: {
        padding: '0.5rem 0.65rem', border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius, fontSize: '0.8rem', fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.colors.surface, boxSizing: 'border-box', width: '100%',
        transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none',
        color: theme.colors.secondary, cursor: 'pointer',
    },
    filterSelectActive: {
        borderColor: theme.colors.primary, boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)',
    },
    clearFiltersButton: {
        padding: '0.4rem 0.85rem', border: 'none',
        borderRadius: theme.borderRadius, fontSize: '0.75rem', fontFamily: theme.typography.fontFamily,
        backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', cursor: 'pointer',
        fontWeight: '500', transition: 'background-color 0.2s', whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', gap: '0.3rem',
    },
    duplicateBadge: {
        display: 'inline-block', marginLeft: '0.4rem', padding: '0.1rem 0.45rem',
        borderRadius: '999px', backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#B45309',
        fontSize: '0.65rem', fontWeight: '700', whiteSpace: 'nowrap', cursor: 'help',
    },
};

// --- Helper function to safely format dates ---
const safeFormatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Handle Firestore Timestamp
    if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString('es-AR');
    }
    // Handle strings or numbers
    try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('es-AR');
        }
    } catch (e) {
        // Fallback for any other case
        return 'Fecha inválida';
    }
    return 'Fecha inválida';
};

// --- SUB-COMPONENTE PARA EL FORMULARIO DE OPERACIÓN ---
const OperacionForm = ({ formState, index, clientesDb, cuentasJefferiesDb, especies, handleInputChange, userRole, isFuturesOperation, mercadosDisponibles, nombresComerciales = [] }) => {
    const isJefferies = formState.mercado === 'JEFFERIES';
    const mercadoOptions = mercadosDisponibles || formOptions.mercadoGeneral;
    const especiesMap = useMemo(() =>
        especies.reduce((acc, esp) => {
            acc[esp.nombre] = esp.moneda;
            return acc;
        }, {}),
        [especies]);

    const sortedEspecies = useMemo(() => {
        const uniqueEspecies = [];
        const seenNames = new Set();

        especies.forEach(esp => {
            const name = esp.nombre || '';
            if (!seenNames.has(name)) {
                seenNames.add(name);
                uniqueEspecies.push(esp);
            }
        });

        return uniqueEspecies.sort((a, b) => {
            const nameA = a.nombre || '';
            const nameB = b.nombre || '';
            return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
        });
    }, [especies]);

    const comitenteOptions = useMemo(() =>
        Object.keys(clientesDb).map(c => ({ value: c, label: formatComitenteForDisplay(c) })),
        [clientesDb]
    );

    const clienteOptions = useMemo(() =>
        Object.entries(clientesDb).map(([value, label]) => ({ value, label: `${label} - ${formatComitenteForDisplay(value)}` })),
        [clientesDb]
    );

    const jefferiesAccountOptions = useMemo(() =>
        Object.entries(cuentasJefferiesDb || {}).map(([accountNumber, accountName]) => ({ value: accountNumber, label: `${accountName} - ${accountNumber}` })),
        [cuentasJefferiesDb]
    );

    const handleEspecieChange = (e) => {
        const ticker = e.target.value.toUpperCase();
        handleInputChange({ target: { name: 'especie', value: ticker } }, index);
        // En Jefferies la moneda queda fija (USD Cable); no la sobreescribe la especie
        if (!isJefferies && especiesMap[ticker]) {
            handleInputChange({ target: { name: 'moneda', value: especiesMap[ticker] } }, index);
        }
    };

    const handleNroComitenteChange = useCallback((selectedOption) => {
        const nroComitente = selectedOption ? selectedOption.value : '';
        const cliente = nroComitente ? clientesDb[nroComitente] : '';
        handleInputChange({ target: { name: 'nroComitente', value: nroComitente } }, index);
        handleInputChange({ target: { name: 'cliente', value: cliente } }, index);
    }, [index, clientesDb, handleInputChange]);

    const handleClienteChange = useCallback((selectedOption) => {
        const nroComitente = selectedOption ? selectedOption.value : '';
        const cliente = nroComitente ? clientesDb[nroComitente] : '';
        handleInputChange({ target: { name: 'nroComitente', value: nroComitente } }, index);
        handleInputChange({ target: { name: 'cliente', value: cliente } }, index);
    }, [index, clientesDb, handleInputChange]);

    // Para Jefferies se reutilizan nroComitente (accountNumber) y cliente (accountName)
    const handleJefferiesAccountChange = useCallback((selectedOption) => {
        const accountNumber = selectedOption ? selectedOption.value : '';
        const accountName = accountNumber ? cuentasJefferiesDb[accountNumber] : '';
        handleInputChange({ target: { name: 'nroComitente', value: accountNumber } }, index);
        handleInputChange({ target: { name: 'cliente', value: accountName } }, index);
    }, [index, cuentasJefferiesDb, handleInputChange]);

    // Al cambiar de/hacia JEFFERIES cambia el dominio de cuentas, moneda y plazo
    const handleMercadoChange = useCallback((e) => {
        const nuevoMercado = e.target.value;
        const seraJefferies = nuevoMercado === 'JEFFERIES';
        handleInputChange({ target: { name: 'mercado', value: nuevoMercado } }, index);
        if (seraJefferies !== isJefferies) {
            handleInputChange({ target: { name: 'nroComitente', value: '' } }, index);
            handleInputChange({ target: { name: 'cliente', value: '' } }, index);
        }
        if (seraJefferies) {
            handleInputChange({ target: { name: 'moneda', value: 'USD Cable' } }, index);
            handleInputChange({ target: { name: 'plazo', value: '' } }, index);
        } else {
            handleInputChange({ target: { name: 'plazo', value: 'C.I.' } }, index);
        }
    }, [index, isJefferies, handleInputChange]);

    useEffect(() => {
        const cantidad = parseFloat(parseAmountForState(formState.cantidadInputValue)) || 0;
        const precio = parseFloat(parseNumberForState(formState.precioInputValue)) || 0;
        const monto = parseFloat(parseAmountForState(formState.montoInputValue)) || 0;
        if (!formState.lastEditedField) return;

        const performUpdate = (fieldName, newValue) => {
            // This check prevents the infinite loop
            if (formState[fieldName] !== newValue) {
                handleInputChange({ target: { name: fieldName, value: newValue } }, index);
            }
        };

        // Jefferies (acciones US): monto = cantidad * precio. Local (bonos): base 100.
        const calcJefferies = formState.mercado === 'JEFFERIES';
        if (formState.lastEditedField === 'cantidad' || formState.lastEditedField === 'precio') {
            if (cantidad > 0 && precio > 0) {
                const montoCalculado = calcJefferies ? (cantidad * precio) : (cantidad * precio) / 100;
                performUpdate('montoInputValue', formatNumberForDisplay(montoCalculado));
            }
        } else if (formState.lastEditedField === 'monto') {
            if (monto > 0 && cantidad > 0) {
                const precioCalculado = calcJefferies ? (monto / cantidad) : (monto * 100) / cantidad;
                performUpdate('precioInputValue', formatNumberForDisplay(precioCalculado, 4));
            } else if (monto > 0 && precio > 0) {
                const cantidadCalculada = calcJefferies ? (monto / precio) : (monto * 100) / precio;
                performUpdate('cantidadInputValue', formatNumberForDisplay(cantidadCalculada));
            }
        }
    }, [formState, index, handleInputChange]);

    return (
        <div className="axon-form-grid" style={{ ...styles.formGrid, borderTop: index > 0 ? `2px dashed ${theme.colors.border}` : 'none', paddingTop: index > 0 ? '1.5rem' : '0' }}>
            {/* --- Sección 1: Mercado y Operación (el mercado define los campos siguientes) --- */}
            <div style={styles.sectionHeader}>Mercado y Operación</div>
            <div style={styles.formGroup}><label style={styles.label}>Mercado</label><select name="mercado" value={formState.mercado} onChange={handleMercadoChange} style={styles.select} required><option value="" disabled>Seleccioná un mercado</option>{mercadoOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div style={styles.formGroup}><label style={styles.label}>Operación</label><select name="operacion" value={formState.operacion} onChange={(e) => handleInputChange(e, index)} style={styles.select}>{formOptions.operacionGeneral.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div style={styles.formGroup}><label style={styles.label}>Plazo</label><select name="plazo" value={formState.plazo} onChange={(e) => handleInputChange(e, index)} style={{ ...styles.select, backgroundColor: (isFuturesOperation || isJefferies) ? theme.colors.border : theme.colors.surface }} disabled={isFuturesOperation || isJefferies}>{isJefferies && <option value="">N/A</option>}{formOptions.plazo.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>

            {/* --- Sección 2: Cuenta (depende del mercado) --- */}
            <div style={styles.sectionHeader}>Cuenta</div>
            {isJefferies ? (
                <div style={styles.formGroupDouble}>
                    <label style={styles.label}>Cuenta Jefferies</label>
                    <Select
                        styles={{ control: (base) => ({ ...base, ...styles.input, padding: '0' }) }}
                        name="jefferiesAccount"
                        required
                        options={jefferiesAccountOptions}
                        value={jefferiesAccountOptions.find(c => c.value === formState.nroComitente) || null}
                        onChange={(selected) => handleJefferiesAccountChange(selected)}
                        placeholder="Buscar cuenta Jefferies..."
                    />
                </div>
            ) : (
                <>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Nro Comitente</label>
                        <Select
                            styles={{ control: (base) => ({ ...base, ...styles.input, padding: '0' }) }}
                            name="nroComitente"
                            required
                            options={comitenteOptions}
                            value={comitenteOptions.find(c => c.value === formState.nroComitente)}
                            onChange={(selected) => handleNroComitenteChange(selected)}
                            placeholder="568 XXXXX"
                        />
                    </div>
                    <div style={styles.formGroupDouble}>
                        <label style={styles.label}>Cliente</label>
                        <Select
                            styles={{ control: (base) => ({ ...base, ...styles.input, padding: '0' }) }}
                            name="cliente"
                            required
                            options={clienteOptions}
                            value={clienteOptions.find(c => c.value === formState.nroComitente)}
                            onChange={(selected) => handleClienteChange(selected)}
                            placeholder="Buscar por nombre de cliente..."
                        />
                    </div>
                </>
            )}

            {/* --- Sección 3: Especie y Valores --- */}
            <div style={styles.sectionHeader}>Especie y Valores</div>
            <div style={styles.formGroupDouble}>
                <label style={styles.label}>Especie</label>
                <input list="especies-list" type="text" name="especie" value={formState.especie} onChange={handleEspecieChange} style={styles.input} autoComplete="off" required />
                <datalist id="especies-list">
                    {sortedEspecies.map(esp => <option key={esp.id} value={esp.nombre} />)}
                </datalist>
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>Moneda</label>
                <select name="moneda" value={formState.moneda} onChange={(e) => handleInputChange(e, index)} style={{ ...styles.select, backgroundColor: (especiesMap[formState.especie] || isJefferies) ? theme.colors.border : theme.colors.surface }} disabled={!!especiesMap[formState.especie] || isJefferies}>
                    {formOptions.monedaGeneral.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
            <div style={styles.formGroup}><label style={styles.label}>Cantidad</label><input type="text" inputMode="decimal" name="cantidadInputValue" value={formState.cantidadInputValue} onChange={(e) => handleInputChange(e, index)} onFocus={(e) => { handleInputChange({ target: { name: 'lastEditedField', value: 'cantidad' } }, index); handleInputChange({ target: { name: 'cantidadInputValue', value: formatNumberForInput(e.target.value) } }, index); }} onBlur={(e) => handleInputChange({ target: { name: 'cantidadInputValue', value: formatNumberForDisplay(parseAmountForState(e.target.value)) } }, index)} style={styles.input} /></div>
            <div style={styles.formGroup}><label style={styles.label}>Precio {!isJefferies && <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: theme.colors.mediumGray }}>(Base 100)</span>}{isJefferies && <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: theme.colors.mediumGray }}>(por acción)</span>}</label><input type="text" inputMode="decimal" name="precioInputValue" value={formState.precioInputValue} onChange={(e) => handleInputChange(e, index)} onFocus={(e) => { handleInputChange({ target: { name: 'lastEditedField', value: 'precio' } }, index); handleInputChange({ target: { name: 'precioInputValue', value: formatNumberForInput(e.target.value) } }, index); }} onBlur={(e) => handleInputChange({ target: { name: 'precioInputValue', value: formatNumberForDisplay(parseNumberForState(e.target.value), 4) } }, index)} style={styles.input} /></div>
            <div style={styles.formGroup}><label style={styles.label}>Monto</label><input type="text" inputMode="decimal" name="montoInputValue" value={formState.montoInputValue} onChange={(e) => handleInputChange(e, index)} onFocus={(e) => { handleInputChange({ target: { name: 'lastEditedField', value: 'monto' } }, index); handleInputChange({ target: { name: 'montoInputValue', value: formatNumberForInput(e.target.value) } }, index); }} onBlur={(e) => handleInputChange({ target: { name: 'montoInputValue', value: formatNumberForDisplay(parseAmountForState(e.target.value)) } }, index)} style={styles.input} /></div>

            {userRole !== 'Comercial' && (
                <div style={styles.formGroupFull}>
                    <label style={styles.label}>Comercial{userRole === 'MiddleOffice' ? '' : ' (opcional)'}</label>
                    <select name="comercial" value={formState.comercial || ''} onChange={(e) => handleInputChange(e, index)} style={styles.select} required={userRole === 'MiddleOffice'}>
                        <option value="">{userRole === 'MiddleOffice' ? 'Seleccioná por cuenta de qué comercial' : 'Sin comercial'}</option>
                        {[...new Set([...nombresComerciales, formState.comercial].filter(Boolean))].map(nombre => (
                            <option key={nombre} value={nombre}>{nombre}</option>
                        ))}
                    </select>
                </div>
            )}

            <div style={styles.formGroupFull}><label style={styles.label}>Comentario Comercial</label><textarea name="comentarioComercial" value={formState.comentarioComercial} onChange={(e) => handleInputChange(e, index)} style={{ ...styles.textarea, backgroundColor: userRole === 'Trader' ? theme.colors.border : theme.colors.surface }} disabled={userRole === 'Trader'} placeholder={userRole === 'Comercial' ? 'Añada un comentario para el trader...' : 'Campo deshabilitado'} /></div>
        </div>
    );
};

/*
 * Props de modo productor (opcionales):
 * - comitentesFilter: lista de nroComitente. Si se pasa, la suscripción se acota a
 *   esos comitentes (única lectura que las reglas le permiten a un productor) y el
 *   filtro de eliminadas se hace en memoria.
 * - mercadosDisponibles: subconjunto de mercados ofrecidos en el formulario.
 *
 * - filtroMisClientes (rol Comercial): filtro de vista "Mis clientes" (ver
 *   saldosScope.filterMisClientes). Se aplica después de numerar, así el "#" de
 *   cada orden es el mismo que ve la mesa.
 */
const OperacionesTab = ({ userRole, userName, userId, clientesDb, cuentasJefferiesDb = {}, especies, titulosA3 = [], onViewComment, comitentesFilter = null, mercadosDisponibles = null, filtroMisClientes = null }) => {
    const [operaciones, setOperaciones] = useState([]);
    // Quien no es comercial elige por cuenta de qué comercial carga la orden.
    const nombresComerciales = useNombresComerciales(userRole !== 'Comercial');
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formError, setFormError] = useState('');
    const [editingOperacion, setEditingOperacion] = useState(null);
    const [rejectionModal, setRejectionModal] = useState({ isOpen: false, op: null });
    const [rejectionComment, setRejectionComment] = useState('');
    const [completionModal, setCompletionModal] = useState({ isOpen: false, op: null });
    const [completionData, setCompletionData] = useState({ comentario: '', cantidadOperada: '', precioOperado: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [a3WarningModal, setA3WarningModal] = useState({ isOpen: false, data: null, pendingAction: null });
    const [duplicateModal, setDuplicateModal] = useState({
        isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null,
    });
    const [filters, setFilters] = useState({});
    const filtersPanel = useMobileCollapse();
    const stackedTable = useStackedTable({
        title: 'Cliente',
        primary: ['ID', 'Operación', 'Especie', 'Cantidad', 'Precio', 'Monto', 'Estado'],
        actions: 'Acciones',
    });

    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => {
            if (!value) {
                const { [field]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [field]: value };
        });
    }, []);

    const clearFilters = useCallback(() => setFilters({}), []);

    const hasActiveFilters = Object.keys(filters).length > 0;

    const createInitialFormState = useCallback(() => ({
        operacion: 'Compra', especie: '', plazo: 'C.I.', nroComitente: '', cliente: '',
        cantidad: 0, precio: 0, monto: 0, moneda: 'Pesos', mercado: '',
        comercial: comercialPorDefecto({ userRole, userName }), trader: '', estado: 'Pendiente',
        comentarioComercial: '', comentarioTrader: '',
        cantidadOperada: 0, precioOperado: 0,
        cantidadInputValue: '', precioInputValue: '', montoInputValue: '',
        lastEditedField: null,
    }), [userName, userRole]);

    const [operacionesForm, setOperacionesForm] = useState([createInitialFormState()]);
    const [isFxOperation, setIsFxOperation] = useState(false);
    const [isFuturesOperation, setIsFuturesOperation] = useState(false);
    const isJefferiesForm = operacionesForm.some(form => form.mercado === 'JEFFERIES');

    // --- Scroll Sync Logic ---
    const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
        useHorizontalScrollSync([operaciones, isLoading]);

    const comitentesKey = comitentesFilter ? [...comitentesFilter].sort().join('|') : null;

    useEffect(() => {
        setIsLoading(true);
        if (comitentesKey !== null) {
            const comitentes = comitentesKey ? comitentesKey.split('|') : [];
            return subscribeByComitentes('operaciones', comitentes, (docs) => {
                setOperaciones(docs.filter(op => op.estado !== 'Eliminada'));
                setIsLoading(false);
            }, () => setIsLoading(false));
        }
        const q = query(collection(db, "operaciones"), where("estado", "!=", "Eliminada"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const operacionesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOperaciones(operacionesData);
            setIsLoading(false);
        }, (error) => { console.error("Error al obtener las operaciones:", error); setIsLoading(false); });
        return () => unsubscribe();
    }, [comitentesKey]);

    const handleFxCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setIsFxOperation(isChecked);
        if (isChecked) {
            setOperacionesForm(prev => [...prev, createInitialFormState()]);
        } else {
            setOperacionesForm(prev => prev.slice(0, 1));
        }
    };

    const handleFuturesCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setIsFuturesOperation(isChecked);
        if (isChecked) {
            setOperacionesForm(prev => prev.map(form => ({ ...form, plazo: 'C.I.' })));
        }
    };

    const operacionesConId = useMemo(() => {
        const sortedByStatusAndDate = [...operaciones].sort((a, b) => {
            // 1. Prioridad de estados: Pendiente/En Proceso/Rechazada primero
            const getPriority = (estado) => {
                if (estado === 'En Proceso' || estado === 'Pendiente' || estado === 'Rechazada') return 0;
                return 1; // Hecha
            };

            const priorityA = getPriority(a.estado);
            const priorityB = getPriority(b.estado);

            if (priorityA !== priorityB) return priorityA - priorityB;

            // 2. Ordenar por fecha (como estaba antes)
            const dateA = a.createdAt?.toDate() || 0;
            const dateB = b.createdAt?.toDate() || 0;
            return dateA - dateB;
        });
        return sortedByStatusAndDate.map((op, index) => ({ ...op, persistentId: index + 1 }));
    }, [operaciones]);

    // docId -> otras operaciones del dia con el mismo comitente, especie, plazo,
    // moneda y mercado.
    const duplicateGroups = useMemo(
        () => buildDuplicateGroups(operacionesConId, getOperacionDuplicateKey),
        [operacionesConId],
    );

    const handleInputChange = useCallback((e, index) => {
        const { name, value } = e.target;
        setOperacionesForm(prevForms => {
            const updatedForms = [...prevForms];
            updatedForms[index] = { ...updatedForms[index], [name]: value };
            return updatedForms;
        });
    }, []);

    // Función para verificar si existe un título A3 para un cliente y especie
    const checkA3Title = useCallback((nroComitente, nombreEspecie) => {
        const titulo = titulosA3.find(t =>
            t.nroComitente === nroComitente && t.nombreEspecie === nombreEspecie
        );
        return titulo || null;
    }, [titulosA3]);

    // Función para ejecutar el guardado después de la verificación A3
    const executeSubmit = async () => {
        try {
            if (editingOperacion) {
                const formState = operacionesForm[0];
                if (!formState.mercado) { setFormError(`Seleccioná un mercado.`); return; }
                const esJefferies = formState.mercado === 'JEFFERIES';
                if (esJefferies) {
                    if (!cuentasJefferiesDb[formState.nroComitente]) { setFormError(`La cuenta Jefferies es inválida.`); return; }
                } else if (!clientesDb[formState.nroComitente]) { setFormError(`El Nro de Comitente es inválido.`); return; }
                const cantidad = parseFloat(parseAmountForState(formState.cantidadInputValue)) || 0;
                const precio = parseFloat(parseNumberForState(formState.precioInputValue)) || 0;
                let monto = parseFloat(parseAmountForState(formState.montoInputValue)) || 0;
                if (cantidad > 0 && precio > 0) monto = esJefferies ? (cantidad * precio) : (cantidad * precio) / 100;
                const { id, persistentId, cantidadInputValue, precioInputValue, montoInputValue, lastEditedField, ...rest } = formState;
                const dataToUpdate = { ...rest, cantidad, precio, monto, estado: 'Pendiente', comentarioTrader: '' };
                if (userRole === 'Comercial') {
                    dataToUpdate.comercial = userName;
                }
                dataToUpdate.esFuturos = isFuturesOperation;
                const docRef = doc(db, "operaciones", editingOperacion.id);
                await updateDoc(docRef, dataToUpdate);
            } else {
                const batch = writeBatch(db);
                for (const formState of operacionesForm) {
                    if (!formState.mercado) { setFormError(`Seleccioná un mercado.`); return; }
                    // Middle office carga en apoyo del área comercial: la orden es de un comercial.
                    if (userRole === 'MiddleOffice' && !formState.comercial) { setFormError('Seleccioná por cuenta de qué comercial cargás la operación.'); return; }
                    const esJefferies = formState.mercado === 'JEFFERIES';
                    if (esJefferies) {
                        if (!cuentasJefferiesDb[formState.nroComitente]) { setFormError(`La cuenta Jefferies es inválida.`); return; }
                    } else if (!clientesDb[formState.nroComitente]) { setFormError(`El Nro de Comitente es inválido.`); return; }
                    const cantidad = parseFloat(parseAmountForState(formState.cantidadInputValue)) || 0;
                    const precio = parseFloat(parseNumberForState(formState.precioInputValue)) || 0;
                    let monto = parseFloat(parseAmountForState(formState.montoInputValue)) || 0;
                    if (cantidad > 0 && precio > 0) monto = esJefferies ? (cantidad * precio) : (cantidad * precio) / 100;
                    const { cantidadInputValue, precioInputValue, montoInputValue, lastEditedField, ...rest } = formState;
                    const dataToSave = {
                        ...rest, cantidad, precio, monto,
                        comercial: userRole === 'Comercial' ? userName : formState.comercial,
                        creadoPorId: userId, creadoPorNombre: userName, createdAt: serverTimestamp(),
                        esFuturos: isFuturesOperation
                    };
                    const docRef = doc(collection(db, "operaciones"));
                    batch.set(docRef, dataToSave);
                }
                await batch.commit();
            }
            setFormError('');
            setIsModalOpen(false);
            setEditingOperacion(null);
            setA3WarningModal({ isOpen: false, data: null, pendingAction: null });
        } catch (error) {
            console.error("Error guardando la operación: ", error);
            setFormError("No se pudo guardar la operación.");
        }
    };

    // Chequeo A3 previo al guardado. Se ejecuta despues del aviso de duplicados.
    const runA3Check = async () => {
        // Verificar si alguna operación tiene títulos en A3 (solo para Ventas)
        for (const formState of operacionesForm) {
            if (!formState.especie || !formState.nroComitente) continue;
            // Los títulos A3 son locales: no aplican a cuentas Jefferies
            if (formState.mercado === 'JEFFERIES') continue;

            // Solo verificar A3 si la operación es de Venta
            if (formState.operacion === 'Venta') {
                const nombreEspecie = formState.especie;
                const tituloA3 = checkA3Title(formState.nroComitente, nombreEspecie);

                if (tituloA3) {
                    // Mostrar modal de alerta
                    setA3WarningModal({
                        isOpen: true,
                        data: tituloA3,
                        pendingAction: executeSubmit
                    });
                    return; // Detener el proceso hasta que el usuario decida
                }
            }
        }

        // Si no hay títulos A3, proceder normalmente
        await executeSubmit();
    };

    // Convierte una fila del formulario en un candidato comparable contra las
    // operaciones ya cargadas.
    const buildDuplicateCandidate = (formState) => {
        const cantidad = parseFloat(parseAmountForState(formState.cantidadInputValue)) || 0;
        const precio = parseFloat(parseNumberForState(formState.precioInputValue)) || 0;
        // Mismo cálculo que executeSubmit, para que el aviso muestre el monto real.
        let monto = parseFloat(parseAmountForState(formState.montoInputValue)) || 0;
        if (cantidad > 0 && precio > 0) {
            monto = formState.mercado === 'JEFFERIES' ? (cantidad * precio) : (cantidad * precio) / 100;
        }
        return {
            operacion: formState.operacion,
            nroComitente: formState.nroComitente,
            cliente: formState.cliente,
            especie: formState.especie,
            plazo: formState.plazo,
            moneda: formState.moneda,
            mercado: formState.mercado,
            monto,
            cantidad,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Avisa (sin bloquear) si ya hay una operación equivalente del día. Cada
        // fila se compara contra lo ya cargado y contra las filas anteriores del
        // mismo alta, para atrapar tambien la linea repetida dentro del batch.
        const candidatosPrevios = [];
        for (const formState of operacionesForm) {
            const candidate = buildDuplicateCandidate(formState);
            const matches = findDuplicates(
                candidate,
                [...operacionesConId, ...candidatosPrevios],
                getOperacionDuplicateKey,
                { excludeId: editingOperacion?.id },
            );
            if (matches.length > 0) {
                setDuplicateModal({
                    isOpen: true,
                    mode: 'alta',
                    matches,
                    candidate,
                    pendingAction: runA3Check,
                });
                return;
            }
            candidatosPrevios.push({ ...candidate, id: `form-${candidatosPrevios.length}`, estado: 'Pendiente' });
        }

        await runA3Check();
    };

    // El aviso acerto: se descarta la carga en curso y se abre la operación
    // existente para corregirla.
    const handleEditFromDuplicate = (match) => {
        setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null });
        handleEditClick(match);
    };

    const openNewOperacionModal = () => {
        setEditingOperacion(null);
        setIsFxOperation(false);
        setIsFuturesOperation(false);
        setOperacionesForm([createInitialFormState()]);
        setFormError('');
        setIsModalOpen(true);
    };

    const handleEditClick = (operacion) => {
        setEditingOperacion(operacion);
        const formState = {
            ...operacion,
            cantidadInputValue: formatNumberForDisplay(operacion.cantidad),
            precioInputValue: formatNumberForDisplay(operacion.precio, 4),
            montoInputValue: formatNumberForDisplay(operacion.monto),
            lastEditedField: null
        };
        setOperacionesForm([formState]);
        setIsFxOperation(false);
        setIsFuturesOperation(operacion.esFuturos || false);
        setIsModalOpen(true);
    };

    const handleDelete = async (operacionId) => {
        if (window.confirm("¿Está seguro de que desea eliminar esta operación?")) {
            const docRef = doc(db, "operaciones", operacionId);
            await updateDoc(docRef, {
                estado: 'Eliminada',
                eliminadoPorId: userId,
                eliminadoPorNombre: userName,
                eliminadoAt: serverTimestamp()
            });
        }
    };

    const handleStatusChange = async (op, newStatus) => {
        if (op.estado === 'Hecha' && op.trader !== userName) {
            alert("Solo el trader asignado puede cambiar el estado de una operación 'Hecha'.");
            return;
        }
        if (userRole === 'Trader') {
            if (newStatus === 'Rechazada') {
                setRejectionModal({ isOpen: true, op: op });
                return;
            }
            if (newStatus === 'Hecha') {
                setCompletionData({
                    comentario: op.comentarioTrader || '',
                    cantidadOperada: formatNumberForDisplay(op.cantidad),
                    precioOperado: formatNumberForDisplay(op.precio, 4),
                });
                setCompletionModal({ isOpen: true, op: op });
                return;
            }
        }
        const payload = { estado: newStatus };
        if (userRole === 'Trader' && newStatus === 'En Proceso') {
            payload.trader = userName;
        } else if (userRole === 'Trader' && newStatus === 'Pendiente') {
            payload.trader = '';
        }
        const docRef = doc(db, "operaciones", op.id);
        await updateDoc(docRef, payload);
    };

    const handleRejectionSubmit = async () => {
        if (!rejectionComment.trim()) {
            alert("El comentario es obligatorio para rechazar una orden.");
            return;
        }
        const docRef = doc(db, "operaciones", rejectionModal.op.id);
        await updateDoc(docRef, { estado: 'Rechazada', trader: userName, comentarioTrader: rejectionComment });
        setRejectionModal({ isOpen: false, op: null });
        setRejectionComment('');
    };

    const handleCompletionDataChange = (e) => {
        const { name, value } = e.target;
        setCompletionData(prev => ({ ...prev, [name]: value }));
    };

    const executeCompletionSubmit = async () => {
        const { op } = completionModal;
        const cantidadOperada = parseFloat(parseAmountForState(completionData.cantidadOperada)) || 0;
        const precioOperado = parseFloat(parseNumberForState(completionData.precioOperado)) || 0;

        let monto = op.monto;
        if (cantidadOperada > 0 && precioOperado > 0) {
            monto = op.mercado === 'JEFFERIES' ? (cantidadOperada * precioOperado) : (cantidadOperada * precioOperado) / 100;
        }

        const docRef = doc(db, "operaciones", op.id);
        await updateDoc(docRef, {
            estado: 'Hecha',
            trader: userName,
            comentarioTrader: completionData.comentario,
            cantidadOperada,
            precioOperado,
            monto
        });

        setCompletionModal({ isOpen: false, op: null });
        setCompletionData({ comentario: '', cantidadOperada: '', precioOperado: '' });
        setA3WarningModal({ isOpen: false, data: null, pendingAction: null });
    };

    const handleCompletionSubmit = async () => {
        const { op } = completionModal;

        // Segunda linea de defensa: completar las dos operaciones de un par
        // duplicado es lo que termina impactando mal en la cuenta del cliente.
        const duplicados = duplicateGroups.get(op?.id) || [];
        if (duplicados.length > 0) {
            setDuplicateModal({
                isOpen: true,
                mode: 'procesamiento',
                matches: duplicados,
                candidate: op,
                pendingAction: runA3CheckOnCompletion,
            });
            return;
        }

        await runA3CheckOnCompletion();
    };

    const runA3CheckOnCompletion = async () => {
        const { op } = completionModal;

        // Verificar si la operación tiene títulos en A3 (solo para Ventas, no aplica a Jefferies)
        if (op.operacion === 'Venta' && op.mercado !== 'JEFFERIES') {
            const nombreEspecie = op.especie;
            const tituloA3 = checkA3Title(op.nroComitente, nombreEspecie);

            if (tituloA3) {
                // Mostrar modal de alerta
                setA3WarningModal({
                    isOpen: true,
                    data: tituloA3,
                    pendingAction: executeCompletionSubmit
                });
                return; // Detener el proceso hasta que el usuario decida
            }
        }

        // Si no hay títulos A3, proceder normalmente
        await executeCompletionSubmit();
        setCompletionData({ comentario: '', cantidadOperada: '', precioOperado: '' });
    };

    // Solo se ofrece editar la existente en un alta simple (no FX) de una unica
    // coincidencia editable, con las mismas condiciones que el lapiz de la fila.
    const matchEditable = duplicateModal.matches[0];
    const canEditFromDuplicate = duplicateModal.mode === 'alta'
        && duplicateModal.matches.length === 1
        && operacionesForm.length === 1
        && !!matchEditable
        && (((userRole === 'Comercial' || userRole === 'MiddleOffice') && (matchEditable.estado === 'Pendiente' || matchEditable.estado === 'Rechazada'))
            || (userRole === 'Trader' && matchEditable.estado === 'Pendiente'));

    const getRowStyle = (estado) => {
        switch (estado) {
            case 'En Proceso': return styles.enProcesoRow;
            case 'Hecha': return styles.hechaRow;
            case 'Rechazada': return styles.rechazadaRow;
            default: return {};
        }
    };

    const tableHeaders = [
        { key: 'persistentId', label: 'ID' },
        { key: 'actions', label: 'Acciones' },
        { key: 'createdAt', label: 'Fecha' },
        { key: 'horaCarga', label: 'Hora', sortable: false, narrow: true, tooltip: 'Hora de carga de la orden' },
        { key: 'operacion', label: 'Operación' }, { key: 'especie', label: 'Especie' },
        { key: 'plazo', label: 'Plazo' }, { key: 'nroComitente', label: 'Nro Comitente' }, { key: 'cliente', label: 'Cliente' },
        { key: 'cantidad', label: 'Cantidad' }, { key: 'cantidadOperada', label: 'Cant. Operada' },
        { key: 'precio', label: 'Precio' }, { key: 'precioOperado', label: 'Precio Operado' },
        { key: 'monto', label: 'Monto' },
        { key: 'moneda', label: 'Moneda' }, { key: 'mercado', label: 'Mercado' }, { key: 'comercial', label: 'Comercial' },
        { key: 'trader', label: 'Trader' }, { key: 'estado', label: 'Estado' },
        { key: 'comentarioComercial', label: 'Com. Comercial' }, { key: 'comentarioTrader', label: 'Com. Trader' }
    ];

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const uniqueComerciales = useMemo(() => {
        const values = new Set(operaciones.map(op => op.comercial).filter(Boolean));
        return [...values].sort();
    }, [operaciones]);

    const uniqueTraders = useMemo(() => {
        const values = new Set(operaciones.map(op => op.trader).filter(Boolean));
        return [...values].sort();
    }, [operaciones]);

    const operacionesVisibles = useMemo(
        () => filterMisClientes(operacionesConId, filtroMisClientes),
        [operacionesConId, filtroMisClientes],
    );

    const filteredOperaciones = useMemo(() => {
        if (!hasActiveFilters) return operacionesVisibles;
        return operacionesVisibles.filter(op => {
            if (filters.operacion && op.operacion !== filters.operacion) return false;
            if (filters.especie && !op.especie?.toLowerCase().includes(filters.especie.toLowerCase())) return false;
            if (filters.nroComitente && !op.nroComitente?.toLowerCase().includes(filters.nroComitente.toLowerCase())) return false;
            if (filters.cliente && !op.cliente?.toLowerCase().includes(filters.cliente.toLowerCase())) return false;
            if (filters.moneda && op.moneda !== filters.moneda) return false;
            if (filters.mercado && op.mercado !== filters.mercado) return false;
            if (filters.comercial && op.comercial !== filters.comercial) return false;
            if (filters.trader && op.trader !== filters.trader) return false;
            if (filters.estado && op.estado !== filters.estado) return false;
            return true;
        });
    }, [operacionesVisibles, filters, hasActiveFilters]);

    const sortedOperaciones = useMemo(() => {
        let sortableItems = [...filteredOperaciones];

        // Helper para prioridad de estado
        const getStatusPriority = (estado) => {
            if (estado === 'En Proceso' || estado === 'Pendiente' || estado === 'Rechazada') return 0;
            return 1; // Hecha
        };

        sortableItems.sort((a, b) => {
            // 1. Siempre ordenar por prioridad de estado primero
            const priorityA = getStatusPriority(a.estado);
            const priorityB = getStatusPriority(b.estado);
            if (priorityA !== priorityB) return priorityA - priorityB;

            // 2. Luego aplicar el ordenamiento configurado por el usuario
            if (sortConfig !== null) {
                return compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction);
            }
            return 0;
        });

        return sortableItems;
    }, [filteredOperaciones, sortConfig]);

    return (
        <div style={styles.container}>
            <div className="axon-wrap" style={{ ...styles.headerContainer, gap: '0.75rem' }}>
                <h2 className="axon-page-title" style={styles.mainTitle}>Planilla de Operaciones</h2>
                <button className="axon-fab" style={styles.button} onClick={openNewOperacionModal}>+ Agregar Operación</button>
            </div>

            {/* Barra de filtros (en el celular arranca plegada) */}
            <div style={styles.filterContainer}>
                <div style={{ ...styles.filterHeader, gap: '0.75rem', ...(filtersPanel.isOpen ? {} : { borderBottom: 'none' }) }}>
                    <CollapsibleHeader isMobile={filtersPanel.isMobile} isOpen={filtersPanel.isOpen} onToggle={filtersPanel.toggle} style={styles.filterHeaderLeft}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                        <h3 style={styles.filterTitle}>Filtros</h3>
                        {hasActiveFilters && (
                            <span style={styles.filterBadge}>{Object.keys(filters).length}</span>
                        )}
                    </CollapsibleHeader>
                    {hasActiveFilters && (
                        <button style={styles.clearFiltersButton} onClick={clearFilters}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            Limpiar filtros
                        </button>
                    )}
                </div>
                {filtersPanel.isOpen && <div className="axon-grid-2" style={styles.filterGrid}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Operación</label>
                        <select style={{ ...styles.filterSelect, ...(filters.operacion ? styles.filterSelectActive : {}) }} value={filters.operacion || ''} onChange={e => handleFilterChange('operacion', e.target.value)}>
                            <option value="">Todas</option>
                            {formOptions.operacionGeneral.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Especie</label>
                        <input style={{ ...styles.filterInput, ...(filters.especie ? styles.filterInputActive : {}) }} type="text" placeholder="Ej: AL30" value={filters.especie || ''} onChange={e => handleFilterChange('especie', e.target.value)} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Nro Comitente</label>
                        <input style={{ ...styles.filterInput, ...(filters.nroComitente ? styles.filterInputActive : {}) }} type="text" placeholder="Ej: 568" value={filters.nroComitente || ''} onChange={e => handleFilterChange('nroComitente', e.target.value)} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Cliente</label>
                        <input style={{ ...styles.filterInput, ...(filters.cliente ? styles.filterInputActive : {}) }} type="text" placeholder="Nombre..." value={filters.cliente || ''} onChange={e => handleFilterChange('cliente', e.target.value)} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Moneda</label>
                        <select style={{ ...styles.filterSelect, ...(filters.moneda ? styles.filterSelectActive : {}) }} value={filters.moneda || ''} onChange={e => handleFilterChange('moneda', e.target.value)}>
                            <option value="">Todas</option>
                            {formOptions.monedaGeneral.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Mercado</label>
                        <select style={{ ...styles.filterSelect, ...(filters.mercado ? styles.filterSelectActive : {}) }} value={filters.mercado || ''} onChange={e => handleFilterChange('mercado', e.target.value)}>
                            <option value="">Todos</option>
                            {formOptions.mercadoGeneral.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Comercial</label>
                        <select style={{ ...styles.filterSelect, ...(filters.comercial ? styles.filterSelectActive : {}) }} value={filters.comercial || ''} onChange={e => handleFilterChange('comercial', e.target.value)}>
                            <option value="">Todos</option>
                            {uniqueComerciales.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Trader</label>
                        <select style={{ ...styles.filterSelect, ...(filters.trader ? styles.filterSelectActive : {}) }} value={filters.trader || ''} onChange={e => handleFilterChange('trader', e.target.value)}>
                            <option value="">Todos</option>
                            {uniqueTraders.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Estado</label>
                        <select style={{ ...styles.filterSelect, ...(filters.estado ? styles.filterSelectActive : {}) }} value={filters.estado || ''} onChange={e => handleFilterChange('estado', e.target.value)}>
                            <option value="">Todos</option>
                            {formOptions.estadoGeneral.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                </div>}
            </div>

            {/* Barra de desplazamiento superior */}
            <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />

            <div
                style={styles.tableContainer}
                ref={contentRef}
                onScroll={onContentScroll}
            >
                {isLoading ? <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando operaciones...</p> :
                    <table ref={stackedTable.ref} className={stackedTable.className} style={styles.table}>
                        <thead>
                            <tr>
                                {tableHeaders.map(header => {
                                    const isSortable = header.sortable !== false;
                                    return (
                                        <th
                                            key={header.key}
                                            style={{ ...styles.th, ...(header.narrow ? styles.narrowCol : {}), cursor: isSortable ? 'pointer' : 'default' }}
                                            onClick={() => isSortable && requestSort(header.key)}
                                            title={header.tooltip || undefined}
                                        >
                                            {header.label}
                                            {isSortable && sortConfig.key === header.key ? (sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽') : ''}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedOperaciones.map((op, index) => {
                                const rowStyle = getRowStyle(op.estado);
                                const canEdit =
                                    ((userRole === 'Comercial' || userRole === 'MiddleOffice') && (op.estado === 'Pendiente' || op.estado === 'Rechazada'))
                                    || (userRole === 'Trader' && op.estado === 'Pendiente');
                                // Middle office elimina las que cargó él en apoyo de un comercial.
                                const canDelete = op.estado === 'Pendiente'
                                    && ((userRole === 'Comercial' && op.comercial === userName)
                                        || (userRole === 'MiddleOffice' && op.creadoPorId === userId));
                                const duplicados = duplicateGroups.get(op.id) || [];
                                const duplicadosIds = duplicados.map(d => '#' + d.persistentId).join(', ');
                                return (
                                    <tr key={op.id} style={rowStyle}>
                                        <td style={styles.td}>
                                            {op.persistentId}
                                            {duplicados.length > 0 && (
                                                <span
                                                    style={styles.duplicateBadge}
                                                    title={`Mismo comitente, especie, plazo y mercado que ${duplicadosIds}. Verificá que no sea la misma operación antes de completarla.`}
                                                >
                                                    ⚠ {duplicadosIds}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <button style={{ ...styles.iconButton, color: canEdit ? theme.colors.primary : '#D1D5DB' }} onClick={() => handleEditClick(op)} disabled={!canEdit} title={canEdit ? "Editar" : "No tiene permisos"}><PencilIcon /></button>
                                            <button style={{ ...styles.iconButton, color: canDelete ? theme.colors.errorText : '#D1D5DB' }} onClick={() => handleDelete(op.id)} disabled={!canDelete} title={canDelete ? "Eliminar" : "No tiene permisos"}><TrashIcon /></button>
                                        </td>
                                        <td style={styles.td}>{safeFormatDate(op.createdAt)}</td>
                                        <td style={{ ...styles.td, ...styles.narrowCol }}>{formatHoraForDisplay(op.createdAt)}</td>
                                        <td style={styles.td}>{op.operacion}</td>
                                        <td style={styles.td} title={op.especie}>{op.especie ? op.especie.substring(0, 10) : ''}</td>
                                        <td style={styles.td}>{op.esFuturos ? `${op.plazo} (Futuros)` : op.plazo}</td>
                                        <td style={styles.td}>{formatComitenteForDisplay(op.nroComitente)}</td>
                                        <td style={styles.td}>{op.cliente}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatNumberForDisplay(op.cantidad)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatNumberForDisplay(op.cantidadOperada)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatPrice(op.precio, op.moneda)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatPrice(op.precioOperado, op.moneda)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(op.monto, op.moneda)}</td>
                                        <td style={styles.td}>{op.moneda}</td>
                                        <td style={styles.td}>{op.mercado}</td>
                                        <td style={styles.td}>{op.comercial}</td>
                                        <td style={styles.td}>{op.trader}</td>
                                        <td style={styles.td}>
                                            {userRole === 'Trader' && (op.estado !== 'Hecha' || op.trader === userName) ? (
                                                <select value={op.estado} onChange={(e) => handleStatusChange(op, e.target.value)} style={{ ...styles.select, marginTop: 0, padding: '0.25rem', backgroundColor: 'rgba(255,255,255,0.6)' }}>
                                                    {formOptions.estadoGeneral.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                            ) : (op.estado)}
                                        </td>
                                        <td style={styles.td}>{renderCommentCell(op.comentarioComercial, 'Comentario Comercial', onViewComment)}</td>
                                        <td style={styles.td}>{renderCommentCell(op.comentarioTrader, 'Comentario del Trader', onViewComment)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                }
            </div>

            <Modal
                isOpen={isModalOpen}
                title={editingOperacion ? 'Editar Operación' : 'Nueva Operación'}
                onSubmit={handleSubmit}
                error={formError}
                footer={<>
                    <button type="button" onClick={() => setIsModalOpen(false)} style={{ ...styles.button, ...styles.secondaryButton }}>Cancelar</button>
                    <button type="submit" style={styles.button}>Guardar</button>
                </>}
            >
                {operacionesForm.map((formState, index) => (
                    <OperacionForm
                        key={index}
                        formState={formState}
                        index={index}
                        clientesDb={clientesDb}
                        cuentasJefferiesDb={cuentasJefferiesDb}
                        especies={especies}
                        handleInputChange={handleInputChange}
                        userRole={userRole}
                        isFuturesOperation={isFuturesOperation}
                        mercadosDisponibles={mercadosDisponibles}
                        nombresComerciales={nombresComerciales}
                    />
                ))}
                <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '0.85rem', marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input type="checkbox" id="fx-checkbox" checked={isFxOperation} onChange={handleFxCheckboxChange} style={{ width: 'auto', marginRight: '0.5rem' }} disabled={!!editingOperacion || isFuturesOperation || isJefferiesForm} />
                        <label htmlFor="fx-checkbox" style={{ ...styles.label, marginTop: 0, marginBottom: 0, color: (!!editingOperacion || isFuturesOperation || isJefferiesForm) ? '#9CA3AF' : theme.colors.secondary }}>Operación de FX (Cargar dos patas)</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input type="checkbox" id="futures-checkbox" checked={isFuturesOperation} onChange={handleFuturesCheckboxChange} style={{ width: 'auto', marginRight: '0.5rem' }} disabled={isFxOperation || isJefferiesForm} />
                        <label htmlFor="futures-checkbox" style={{ ...styles.label, marginTop: 0, marginBottom: 0, color: (isFxOperation || isJefferiesForm) ? '#9CA3AF' : theme.colors.secondary }}>Operación de Futuros</label>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={rejectionModal.isOpen}
                title="Motivo de Rechazo"
                maxWidth="500px"
                footer={<>
                    <button onClick={() => setRejectionModal({ isOpen: false, op: null })} style={{ ...styles.button, ...styles.secondaryButton }}>Cancelar</button>
                    <button onClick={handleRejectionSubmit} style={styles.button} disabled={!rejectionComment.trim()}>Confirmar Rechazo</button>
                </>}
            >
                <p style={{ margin: '0 0 1rem 0', color: theme.colors.secondary }}>Por favor, ingrese el motivo por el cual se rechaza esta operación.</p>
                <textarea value={rejectionComment} onChange={(e) => setRejectionComment(e.target.value)} style={styles.textarea} autoFocus></textarea>
            </Modal>

            <Modal
                isOpen={completionModal.isOpen}
                title="Completar Operación"
                maxWidth="600px"
                footer={<>
                    <button onClick={() => setCompletionModal({ isOpen: false, op: null })} style={{ ...styles.button, ...styles.secondaryButton }}>Cancelar</button>
                    <button onClick={handleCompletionSubmit} style={styles.button}>Confirmar y Marcar como Hecha</button>
                </>}
            >
                <p style={{ margin: '0 0 1.5rem 0', color: theme.colors.secondary }}>
                    Defina la cantidad y precio operado. Puede añadir un comentario opcional.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={styles.formGroupHalf}>
                        <label style={styles.label}>Cantidad Operada</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            name="cantidadOperada"
                            value={completionData.cantidadOperada}
                            onChange={handleCompletionDataChange}
                            onFocus={(e) => handleCompletionDataChange({ target: { name: 'cantidadOperada', value: formatNumberForInput(e.target.value) } })}
                            onBlur={(e) => handleCompletionDataChange({ target: { name: 'cantidadOperada', value: formatNumberForDisplay(parseAmountForState(e.target.value)) } })}
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.formGroupHalf}>
                        <label style={styles.label}>Precio Operado</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            name="precioOperado"
                            value={completionData.precioOperado}
                            onChange={handleCompletionDataChange}
                            onFocus={(e) => handleCompletionDataChange({ target: { name: 'precioOperado', value: formatNumberForInput(e.target.value) } })}
                            onBlur={(e) => handleCompletionDataChange({ target: { name: 'precioOperado', value: formatNumberForDisplay(parseNumberForState(e.target.value), 4) } })}
                            style={styles.input}
                        />
                    </div>
                </div>
                <div style={styles.formGroupFull}>
                    <label style={styles.label}>Comentario</label>
                    <textarea
                        name="comentario"
                        value={completionData.comentario}
                        onChange={handleCompletionDataChange}
                        style={styles.textarea}
                        placeholder='Comentario opcional...'
                    />
                </div>
            </Modal>

            {/* Modal de alerta de títulos A3 */}
            <A3WarningModal
                isOpen={a3WarningModal.isOpen}
                data={a3WarningModal.data}
                onContinue={() => {
                    if (a3WarningModal.pendingAction) {
                        a3WarningModal.pendingAction();
                    }
                }}
                onCancel={() => {
                    setA3WarningModal({ isOpen: false, data: null, pendingAction: null });
                }}
            />

            <DuplicateWarningModal
                isOpen={duplicateModal.isOpen}
                mode={duplicateModal.mode}
                entity="operacion"
                candidate={duplicateModal.candidate}
                matches={duplicateModal.matches}
                onEditMatch={canEditFromDuplicate ? handleEditFromDuplicate : undefined}
                onContinue={() => {
                    const action = duplicateModal.pendingAction;
                    setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null });
                    action?.();
                }}
                onCancel={() => setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null })}
            />
        </div>
    );
};

export default OperacionesTab;
